import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Plus, Users, UserPlus, Eye, Save, Edit, PlusCircle, Search, X, Upload, FileText,
  Building2, Phone, Mail, CreditCard, Calendar, TrendingUp, Package, ShoppingCart,
  HelpCircle, MapPin, Hash, Globe, User, Calculator, Percent, Handshake, Link,
  Copy, RefreshCw, ExternalLink, CheckCircle, Download, FileSpreadsheet, AlertCircle,
  Palette, RotateCcw, ImageIcon,
} from 'lucide-react';
import { AuthContext } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import './ClientManagement.css';
import SearchInput from '../components/SearchInput';
import { useNavigate } from 'react-router-dom';
import ExcelJS from 'exceljs';

import ClientsList from './ClientsList';
import StockApiTokenSection from '../components/StockApiTokenSection';

const API_URL = process.env.REACT_APP_API_URL;

// ─── Colores preestablecidos ────────────────────────────────────────────────
const PRESET_COLORS = [
  { label: 'Verde (defecto)', value: '#3cbf9a' },
  { label: 'Azul marino',     value: '#26428E' },
  { label: 'Lima',            value: '#CCDC00' },
  { label: 'Índigo',          value: '#4f46e5' },
  { label: 'Rojo',            value: '#ef4444' },
  { label: 'Naranja',         value: '#f97316' },
  { label: 'Púrpura',         value: '#9333ea' },
  { label: 'Rosa',            value: '#ec4899' },
  { label: 'Gris oscuro',     value: '#374151' },
  { label: 'Negro',           value: '#111827' },
];

// ─── Validador hex robusto ───────────────────────────────────────────────────
const isValidHex = (c) =>
  typeof c === 'string' && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(c.trim());

// ─── Selector de color ───────────────────────────────────────────────────────
function ColorPickerField({ label, value, defaultColor = '#3cbf9a', onChange, helpText }) {
  const [showPresets, setShowPresets] = useState(false);
  const safeValue = isValidHex(value) ? value.trim() : defaultColor;

  return (
    <div className="relative">
      <label className="block text-sm font-bold text-gray-700 mb-2">
        <Palette className="w-4 h-4 inline mr-2" />
        {label}
      </label>
      <div className="flex items-center gap-2">
        <div
          className="w-10 h-10 rounded-lg border-2 border-gray-300 flex-shrink-0 cursor-pointer shadow-sm"
          style={{ backgroundColor: safeValue }}
          onClick={() => setShowPresets(!showPresets)}
          title="Ver colores preestablecidos"
        />
        <input
          type="color"
          value={safeValue}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded cursor-pointer border border-gray-300 p-0.5 flex-shrink-0"
          title="Elegir color personalizado"
        />
        <input
          type="text"
          value={value || ''}
          maxLength={7}
          placeholder="#3cbf9a"
          onChange={(e) => {
            let v = e.target.value.trim();
            if (v && !v.startsWith('#')) v = `#${v}`;
            onChange(v);
          }}
          className={`flex-1 px-3 py-2 border-2 rounded-lg focus:outline-none font-mono text-sm ${
            value && !isValidHex(value) ? 'border-red-400' : 'border-gray-300'
          }`}
          onFocus={(e) => (e.target.style.borderColor = '#3cbf9a')}
          onBlur={(e) =>
            (e.target.style.borderColor =
              value && !isValidHex(value) ? '#f87171' : '#e5e7eb')
          }
        />
        <button
          type="button"
          onClick={() => onChange(defaultColor)}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title={`Restablecer a ${defaultColor}`}
        >
          <RotateCcw size={16} />
        </button>
      </div>
      {value && !isValidHex(value) && (
        <p className="text-red-500 text-xs mt-1">Formato hex inválido (ej: #3cbf9a)</p>
      )}
      {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
      {showPresets && (
        <div className="absolute z-[200] top-full left-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-72">
          <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
            Colores preestablecidos
          </p>
          <div className="grid grid-cols-5 gap-2">
            {PRESET_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => { onChange(c.value); setShowPresets(false); }}
                className="group flex flex-col items-center"
                title={c.label}
              >
                <div
                  className={`w-8 h-8 rounded-lg border-2 transform transition-transform group-hover:scale-110 ${
                    safeValue === c.value ? 'border-gray-800 scale-110' : 'border-gray-200'
                  }`}
                  style={{ backgroundColor: c.value }}
                />
                <span className="text-[9px] text-gray-500 mt-1 leading-tight text-center">
                  {c.label}
                </span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowPresets(false)}
            className="mt-3 w-full text-xs text-gray-500 hover:text-gray-700 py-1"
          >
            Cerrar
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Campo de Logo (subida de archivo a BD) ──────────────────────────────────
function LogoUploadField({ clientId, logoArchivoId, logoFilename, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState('');
  const [previewError, setPreviewError] = useState(false);
  const fileInputRef = useRef(null);

  // URL de la vista previa: prioridad archivo BD > filename legacy en /logos/
  const previewSrc = logoArchivoId
    ? `${API_URL}/api/archivos/${logoArchivoId}`
    : logoFilename
      ? `${API_URL}/logos/${logoFilename}`
      : null;

  useEffect(() => { setPreviewError(false); }, [logoArchivoId, logoFilename]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');

    if (!file.type.startsWith('image/')) {
      setError('El archivo debe ser una imagen.');
      return;
    }

    try {
      setUploading(true);
      const token    = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/clientes/${clientId}/logo`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` }, // sin Content-Type: lo pone el navegador con el boundary
        body:    formData,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'No se pudo subir el logo.');
      }

      const data = await response.json();
      onUploaded(data.logo_archivo_id);
    } catch (err) {
      setError(err.message || 'Error al subir el logo.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <label className="block text-sm font-bold text-gray-700 mb-2">
        <ImageIcon className="w-4 h-4 inline mr-2" />
        Archivo de logo
      </label>

      <div className="flex items-center gap-4 border-2 border-gray-200 rounded-xl p-4 bg-gray-50">
        {/* Vista previa */}
        <div
          className="w-20 h-20 rounded-lg border border-gray-200 flex items-center justify-center flex-shrink-0 bg-white overflow-hidden"
          title={previewSrc ? 'Logo actual' : 'Sin logo'}
        >
          {previewSrc && !previewError ? (
            <img
              src={previewSrc}
              alt="Logo del cliente"
              className="max-w-full max-h-full object-contain p-1"
              onError={() => setPreviewError(true)}
            />
          ) : (
            <ImageIcon size={28} className="text-gray-300" />
          )}
        </div>

        {/* Acción */}
        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={uploading}
            className="hidden"
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white shadow-sm disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: '#3cbf9a' }}
          >
            {uploading ? (
              <>
                <RefreshCw size={16} className="mr-2 animate-spin" />
                Subiendo…
              </>
            ) : (
              <>
                <Upload size={16} className="mr-2" />
                {previewSrc ? 'Cambiar logo' : 'Subir logo'}
              </>
            )}
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Selecciona una imagen (PNG, JPG, SVG). Se aplicará a este cliente y a sus
            centros asociados. Déjalo vacío para usar el logo por defecto.
          </p>
          {error && (
            <p className="text-red-500 text-xs mt-2 flex items-center">
              <AlertCircle size={12} className="mr-1" />
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ClientManagement() {
  const { user } = useContext(AuthContext);
  // ─── TEMA DINÁMICO ───────────────────────────────────────────────────
  const { theme } = useTheme();
  /** Estilo inline para fondos primarios */
  const bgPrimary       = { backgroundColor: theme.primary };
  /** Estilo inline para fondos primarios claros */
  const bgPrimaryLight  = { backgroundColor: theme.primaryLight };
  /** Estilo inline para fondos primarios oscuros */
  const bgPrimaryDark   = { backgroundColor: theme.primaryDark };
  /** Texto con color primario */
  const textPrimary     = { color: theme.primary };
  /** Texto con color primario oscuro */
  const textPrimaryDark = { color: theme.primaryDark };
  /** Borde con color primario */
  const borderPrimary   = { borderColor: theme.primary };
  // ─────────────────────────────────────────────────────────────────────
  const [activeClient, setActiveClient] = useState(null);
  const [showProviderSearch, setShowProviderSearch] = useState(false);
  const [providerSearchInput, setProviderSearchInput] = useState('');
  const [providerSearchResults, setProviderSearchResults] = useState([]);
  const [providers, setProviders] = useState([]);
  const [editedProviders, setEditedProviders] = useState({});
  const [clientSearchInput, setClientSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [linkedClients, setLinkedClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  const [showProviderModal, setShowProviderModal] = useState(false);
  const [tableSearchFilter, setTableSearchFilter] = useState('');
  const [showExcelHelpModal, setShowExcelHelpModal] = useState(false);
  const [showClientsList, setShowClientsList] = useState(true);
  const [loadingCatalogo, setLoadingCatalogo] = useState(false);
  // Catálogo: modal de filtros (proveedores + categorías). Vacío = todos.
  const [showCatalogoModal, setShowCatalogoModal] = useState(false);
  const [selProvIds, setSelProvIds] = useState([]);
  const [selCategorias, setSelCategorias] = useState([]);

  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [savingClient, setSavingClient] = useState(false);
  const [formasPago, setFormasPago] = useState([]);

  const [showTokenModal, setShowTokenModal] = useState(false);
  const [calendarToken, setCalendarToken] = useState(null);
  const [tokenCreatedAt, setTokenCreatedAt] = useState(null);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [integrationFile, setIntegrationFile] = useState(null);
  const [uploadingIntegration, setUploadingIntegration] = useState(false);
  const [integrationMessage, setIntegrationMessage] = useState('');
  const [integrationError, setIntegrationError] = useState('');
  const [integrationResults, setIntegrationResults] = useState(null);
  const [isIntegrationDragOver, setIsIntegrationDragOver] = useState(false);

  const [syncingMatriz, setSyncingMatriz] = useState(false);

  // ── Límites de pedido por usuario ──────────────────────────────────────────
  const [showLimitesModal, setShowLimitesModal] = useState(false);
  const [limitesUsuarios, setLimitesUsuarios] = useState([]);
  const [limitesLoading, setLimitesLoading] = useState(false);
  const [limitesSaving, setLimitesSaving] = useState(false);
  const [limitesError, setLimitesError] = useState('');
  // Candidatos a aprobador (red de la clínica + admins) para el desplegable del modal.
  const [aprobadoresDisponibles, setAprobadoresDisponibles] = useState([]);

  // Trae los usuarios de la clínica con su rango [min, max] y su aprobador actual.
  const fetchLimites = async (clientId) => {
    setLimitesLoading(true);
    setLimitesError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/limites-pedido/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al cargar los límites');
      const data = await response.json();
      setLimitesUsuarios((data.usuarios || []).map((u) => ({
        ...u,
        importe_min: u.importe_min === null || u.importe_min === undefined ? '' : String(u.importe_min),
        importe_max: u.importe_max === null || u.importe_max === undefined ? '' : String(u.importe_max),
        aprobador_id: u.aprobador_id === null || u.aprobador_id === undefined ? '' : String(u.aprobador_id),
      })));
      // Candidatos a aprobador para esta clínica (usuarios de la red + admins).
      const respApro = await fetch(`${API_URL}/api/usuarios/aprobadores?clinica_id=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const dataApro = respApro.ok ? await respApro.json() : { aprobadores: [] };
      setAprobadoresDisponibles(dataApro.aprobadores || []);
    } catch (e) {
      setLimitesError(e.message || 'Error al cargar los límites');
      setLimitesUsuarios([]);
    } finally {
      setLimitesLoading(false);
    }
  };

  const handleLimiteChange = (usuarioId, field, value) => {
    // Solo números y separador decimal.
    const clean = value.replace(',', '.').replace(/[^0-9.]/g, '');
    setLimitesUsuarios((prev) => prev.map((u) => (u.id === usuarioId ? { ...u, [field]: clean } : u)));
  };

  // Cambio del aprobador de un usuario (valor tal cual, sin limpieza numérica).
  const handleAprobadorChange = (usuarioId, value) => {
    setLimitesUsuarios((prev) => prev.map((u) => (u.id === usuarioId ? { ...u, aprobador_id: value } : u)));
  };

  // Valida no-solapamiento en el cliente antes de mandar (mismo criterio que el backend).
  const validarLimites = () => {
    const rangos = limitesUsuarios
      .map((u) => ({
        nombre: u.nombrec || u.username,
        min: u.importe_min === '' ? 0 : Number(u.importe_min),
        max: u.importe_max === '' ? null : Number(u.importe_max),
      }))
      .filter((r) => r.max !== null || r.min > 0);
    for (const r of rangos) {
      if (r.max !== null && r.min >= r.max) return `${r.nombre}: el mínimo debe ser menor que el máximo.`;
    }
    const ord = [...rangos].sort((a, b) => a.min - b.min);
    for (let i = 1; i < ord.length; i++) {
      const prevMax = ord[i - 1].max === null ? Infinity : ord[i - 1].max;
      // El siguiente tramo debe empezar POR ENCIMA del máximo del anterior (no puede tocarlo).
      if (ord[i].min <= prevMax) {
        return `Los rangos se solapan: un tramo empieza en ${ord[i].min} € pero el anterior llega hasta ${prevMax} €. El siguiente debe empezar por encima de ${prevMax} €.`;
      }
    }
    return null;
  };

  const saveLimites = async () => {
    const err = validarLimites();
    if (err) { setLimitesError(err); return; }
    setLimitesSaving(true);
    setLimitesError('');
    try {
      const token = localStorage.getItem('token');
      const limites = limitesUsuarios.map((u) => ({
        usuario_id: u.id,
        importe_min: u.importe_min === '' ? 0 : Number(u.importe_min),
        importe_max: u.importe_max === '' ? null : Number(u.importe_max),
        aprobador_id: u.aprobador_id === '' || u.aprobador_id === undefined ? null : Number(u.aprobador_id),
      }));
      const response = await fetch(`${API_URL}/api/limites-pedido/${activeClient.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ limites }),
      });
      if (!response.ok) {
        const d = await response.json().catch(() => ({}));
        throw new Error(d.message || 'Error al guardar los límites');
      }
      setShowLimitesModal(false);
    } catch (e) {
      setLimitesError(e.message || 'Error al guardar los límites');
    } finally {
      setLimitesSaving(false);
    }
  };

  const isAdmin  = user?.permisos === 1;
  const isClient = user?.permisos === 5;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    loadFormasPago();
    if ((isClient && user.clinica_matriz) || isAdmin) {
      const savedClient = sessionStorage.getItem('activeClient');
      if (savedClient) {
        try {
          const client = JSON.parse(savedClient);
          setActiveClient(client);
          fetchLinkedClients(client.id, client.clinica_matriz);
        } catch (err) {
          console.error('Error al recuperar el cliente de la sesión:', err);
        }
      }
    }
  }, [user, isAdmin, isClient]);

  const navigate = useNavigate();

  const loadFormasPago = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/formas-pago`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setFormasPago(data.formas_pago || []);
      }
    } catch (error) {
      console.error('Error al cargar formas de pago:', error);
    }
  };

  const handleClientSelected = (client) => {
    setActiveClient(client);
    sessionStorage.setItem('activeClient', JSON.stringify(client));
    fetchLinkedClients(client.id, client.clinica_matriz);
    fetchClientProviders(client.id);
    setShowClientsList(false);
  };

  const handleDescargarCatalogo = async ({ proveedores = [], categorias = [] } = {}) => {
    if (!activeClient) return;
    try {
      setLoadingCatalogo(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (proveedores.length) params.append('proveedores', proveedores.join(','));
      if (categorias.length)  params.append('categorias', categorias.join(','));
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API_URL}/api/clientes/${activeClient.id}/catalogo${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al generar catálogo');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none'; a.href = url;
      a.download = `catalogo_${activeClient.razon_social.replace(/[^A-Za-z0-9]/g, '_')}.xlsx`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
      setShowCatalogoModal(false);
    } catch { console.error('Error al descargar catálogo'); }
    finally { setLoadingCatalogo(false); }
  };

  // Abre el modal de filtros del catálogo. Las categorías son fijas; los
  // proveedores salen del estado `providers` ya cargado al elegir el cliente.
  const abrirCatalogoModal = () => {
    if (!activeClient) return;
    setSelProvIds([]); setSelCategorias([]);
    setShowCatalogoModal(true);
  };

  const handleNuevoPedido    = () => navigate('/nuevo-pedido');
  const handleAddProduct     = () => {
    if (!activeClient) { alert('Por favor seleccione un cliente antes de añadir productos'); return; }
    sessionStorage.setItem('activeClient', JSON.stringify(activeClient));
    navigate('/add-product');
  };
  const handleUpdateProduct  = () => {
    if (!activeClient) { alert('Por favor seleccione un cliente antes de actualizar'); return; }
    sessionStorage.setItem('activeClient', JSON.stringify(activeClient));
    navigate('/update-products');
  };

  // ── Integración Hispafarma ──────────────────────────────────────────────────
  const handleDownloadTemplate = async () => {
    try {
      const workbook  = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Integración Hispafarma');
      worksheet.columns = [
        { header: 'ID Cliente CentralSalud',       key: 'id_centralsalud',      width: 25 },
        { header: 'ID Cliente Hispafarma (Cirugía)', key: 'id_hispafarma',       width: 32 },
        { header: 'ID Medicina Estética (Opcional)', key: 'id_medicinaestetica', width: 32 },
      ];
      worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      worksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF3CBF9A' } };
      worksheet.getRow(1).alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(1).height = 25;
      worksheet.getRow(1).eachCell((cell) => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
      const exampleData = [
        { id_centralsalud: 123, id_hispafarma: 999999, id_medicinaestetica: 888888 },
        { id_centralsalud: 456, id_hispafarma: 777777, id_medicinaestetica: 666666 },
        { id_centralsalud: 789, id_hispafarma: 555555, id_medicinaestetica: '' },
      ];
      exampleData.forEach((data, index) => {
        const row = worksheet.addRow(data);
        if (index % 2 === 0) row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
            left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
            bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
            right: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
      });
      worksheet.addRow([]);
      const noteRow = worksheet.addRow(['NOTA:', 'Los campos ID Cliente CentralSalud e ID Cliente Hispafarma son OBLIGATORIOS', '']);
      noteRow.getCell(1).font = { bold: true, color: { argb: 'FFFF0000' } };
      noteRow.getCell(2).font = { italic: true };
      const noteRow2 = worksheet.addRow(['', 'El campo ID Medicina Estética es OPCIONAL', '']);
      noteRow2.getCell(2).font = { italic: true, color: { argb: 'FF666666' } };
      const buffer = await workbook.xlsx.writeBuffer();
      const blob   = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link   = document.createElement('a');
      const url    = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'plantilla_integracion_hispafarma.xlsx');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al generar plantilla Excel:', error);
      alert('Error al generar la plantilla de Excel');
    }
  };

  const handleIntegrationFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isValid = ['.xlsx', '.xls'].some(ext => file.name.toLowerCase().endsWith(ext));
      if (!isValid) { setIntegrationError('Por favor seleccione un archivo Excel (.xlsx o .xls)'); return; }
      setIntegrationFile(file);
      setIntegrationError('');
    }
    e.target.value = '';
  };

  const handleIntegrationDragOver  = (e) => { e.preventDefault(); setIsIntegrationDragOver(true); };
  const handleIntegrationDragLeave = (e) => { e.preventDefault(); setIsIntegrationDragOver(false); };
  const handleIntegrationDrop      = (e) => {
    e.preventDefault();
    setIsIntegrationDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file    = files[0];
      const isValid = ['.xlsx', '.xls'].some(ext => file.name.toLowerCase().endsWith(ext));
      if (!isValid) { setIntegrationError('Por favor seleccione un archivo Excel (.xlsx o .xls)'); return; }
      setIntegrationFile(file);
      setIntegrationError('');
    }
  };

  const handleUploadIntegration = async () => {
    if (!integrationFile) { setIntegrationError('Por favor seleccione un archivo'); return; }
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!validTypes.includes(integrationFile.type)) {
      setIntegrationError('Por favor seleccione un archivo Excel (.xlsx o .xls)');
      return;
    }
    try {
      setUploadingIntegration(true);
      setIntegrationError('');
      setIntegrationMessage('');
      setIntegrationResults(null);
      const formData = new FormData();
      formData.append('file', integrationFile);
      const token    = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clientes/bulk-hispafarma-ids`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al procesar el archivo');
      setIntegrationResults(data);
      setIntegrationMessage(`✅ Integración completada. ${data.updated} clientes actualizados correctamente.`);
      setIntegrationFile(null);
    } catch (error) {
      console.error('Error en integración:', error);
      setIntegrationError(error.message || 'Error al procesar el archivo');
    } finally {
      setUploadingIntegration(false);
    }
  };

  const handleCloseIntegrationModal = () => {
    setShowIntegrationModal(false);
    setIntegrationFile(null);
    setIntegrationError('');
    setIntegrationMessage('');
    setIntegrationResults(null);
    setIsIntegrationDragOver(false);
  };

  // ── Token de Calendario ────────────────────────────────────────────────────
  const loadCalendarToken = async (clientId) => {
    try {
      const token    = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/citas/clientes/${clientId}/token-calendario`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCalendarToken(data.token);
        setTokenCreatedAt(data.created_at);
      } else {
        setCalendarToken(null);
        setTokenCreatedAt(null);
      }
    } catch (error) {
      console.error('Error al cargar token:', error);
      setCalendarToken(null);
      setTokenCreatedAt(null);
    }
  };

  const handleGenerateToken = async () => {
    if (!activeClient) return;
    const confirmMessage = calendarToken
      ? '¿Estás seguro de generar un nuevo token? El token actual dejará de funcionar.'
      : '¿Deseas generar un token para el calendario público de este cliente?';
    if (!window.confirm(confirmMessage)) return;
    try {
      setGeneratingToken(true);
      const token    = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/citas/clientes/${activeClient.id}/generar-token-calendario`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al generar token');
      }
      const data = await response.json();
      setCalendarToken(data.token);
      setTokenCreatedAt(new Date().toISOString());
      alert('Token generado correctamente');
    } catch (error) {
      console.error('Error al generar token:', error);
      alert(error.message || 'Error al generar token');
    } finally {
      setGeneratingToken(false);
    }
  };

  const handleCopyToken = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      alert('No se pudo copiar al portapapeles');
    }
  };

  const handleOpenTokenModal = () => {
    if (!activeClient) return;
    loadCalendarToken(activeClient.id);
    setShowTokenModal(true);
  };

  // ── Edición de Cliente ─────────────────────────────────────────────────────
  const handleEditClient = async () => {
    if (!activeClient) return;
    setEditingClient(activeClient);

    // Precarga de personalización (colores + logo): el objeto activeClient puede no
    // traer estos campos, así que consultamos el endpoint /theme para rellenarlos.
    let themeData = {
      color_primario:   activeClient.color_primario,
      color_secundario: activeClient.color_secundario,
      logo_filename:    activeClient.logo_filename,
      logo_archivo_id:  activeClient.logo_archivo_id,
    };
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`${API_URL}/api/clientes/${activeClient.id}/theme`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const t = await res.json();
        themeData = {
          color_primario:   t.color_primario   ?? themeData.color_primario,
          color_secundario: t.color_secundario ?? themeData.color_secundario,
          logo_filename:    t.logo_filename    ?? themeData.logo_filename,
          logo_archivo_id:  t.logo_archivo_id  ?? themeData.logo_archivo_id,
        };
      }
    } catch (err) {
      console.error('Error al cargar el theme del cliente:', err);
    }

    setEditFormData({
      razon_social:     activeClient.razon_social     || '',
      alias:            activeClient.alias            || '',
      cif:              activeClient.cif              || '',
      calle:            activeClient.calle            || '',
      localidad:        activeClient.localidad        || '',
      provincia:        activeClient.provincia        || '',
      c_postal:         activeClient.c_postal != null ? String(activeClient.c_postal) : '',
      telefono1:        activeClient.telefono1        || '',
      telefono2:        activeClient.telefono2        || '',
      mail:             activeClient.mail             || '',
      mail_admin:       activeClient.mail_admin       || '',
      mail_admin_2:     activeClient.mail_admin_2     || '',
      fax:              activeClient.fax              || '',
      contacto:         activeClient.contacto         || '',
      cuenta_contable:  activeClient.cuenta_contable  || '',
      dia_pago:         activeClient.dia_pago         || '',
      comision:         activeClient.comision         || '0',
      modelo_negocio:   activeClient.modelo_negocio   || '',
      modneg_detalle:   activeClient.modneg_detalle   || '',
      ahorro:           activeClient.ahorro           || '0',
      iban:             activeClient.iban             || '',
      email_calendario: activeClient.email_calendario || '',
      tipo_calendario:  activeClient.tipo_calendario  || 'ninguno',
      color_primario:   themeData.color_primario   || '#3cbf9a',
      color_secundario: themeData.color_secundario || '#1a73e8',
      logo_filename:    themeData.logo_filename    || '',   // ← compatibilidad
      logo_archivo_id:  themeData.logo_archivo_id  || null, // ← logo en BD
      gestion_stock:    activeClient.gestion_stock || 'inactivo',
      pedidos_pendientes: !!activeClient.pedidos_pendientes,
      gestion_albaranes: !!activeClient.gestion_albaranes,
      notif_incidencias: activeClient.notif_incidencias == null ? true : !!activeClient.notif_incidencias,
    });
    setEditErrors({});
    setShowEditClientModal(true);
  };

  const handleEditInputChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
    if (editErrors[field]) setEditErrors(prev => ({ ...prev, [field]: '' }));
    if (field === 'tipo_calendario' && value !== 'ninguno') {
      if (!editFormData.email_calendario) {
        setEditErrors(prev => ({ ...prev, email_calendario: 'Debes ingresar un email de calendario' }));
      }
    } else if (field === 'tipo_calendario' && value === 'ninguno') {
      setEditFormData(prev => ({ ...prev, email_calendario: '' }));
      setEditErrors(prev => ({ ...prev, email_calendario: '' }));
    }
  };

  const validateEditForm = () => {
    const newErrors = {};
    if (!editFormData.razon_social?.trim()) newErrors.razon_social = 'La razón social es requerida';
    if (!editFormData.cif?.trim()) newErrors.cif = 'El CIF es requerido';
    if (!editFormData.calle?.trim()) newErrors.calle = 'La calle es requerida';
    if (!editFormData.localidad?.trim()) newErrors.localidad = 'La localidad es requerida';
    const cPostalStr = editFormData.c_postal != null ? String(editFormData.c_postal).trim() : '';
    if (!cPostalStr) newErrors.c_postal = 'El código postal es requerido';
    if (editFormData.mail && !/\S+@\S+\.\S+/.test(editFormData.mail))
      newErrors.mail = 'El email no es válido';
    if (cPostalStr && (cPostalStr.length !== 5 || isNaN(cPostalStr)))
      newErrors.c_postal = 'El código postal debe tener 5 dígitos';
    if (editFormData.tipo_calendario !== 'ninguno') {
      if (!editFormData.email_calendario?.trim()) {
        newErrors.email_calendario = 'El email de calendario es requerido';
      } else if (!/\S+@\S+\.\S+/.test(editFormData.email_calendario)) {
        newErrors.email_calendario = 'El email de calendario no es válido';
      } else {
        const domain = editFormData.email_calendario.split('@')[1]?.toLowerCase();
        if (editFormData.tipo_calendario === 'google' &&
            !domain?.includes('gmail.com') && !domain?.includes('googlemail.com'))
          newErrors.email_calendario = 'Para Google Calendar, usa un email @gmail.com';
        else if (editFormData.tipo_calendario === 'outlook' &&
            !domain?.includes('outlook.com') && !domain?.includes('hotmail.com') && !domain?.includes('live.com'))
          newErrors.email_calendario = 'Para Outlook Calendar, usa un email @outlook.com, @hotmail.com o @live.com';
        else if (editFormData.tipo_calendario === 'icloud' &&
            !domain?.includes('icloud.com') && !domain?.includes('me.com'))
          newErrors.email_calendario = 'Para iCloud Calendar, usa un email @icloud.com o @me.com';
      }
    }
    if (editFormData.color_primario && !isValidHex(editFormData.color_primario))
      newErrors.color_primario = 'Color primario inválido (formato hex, ej: #3cbf9a)';
    if (editFormData.color_secundario && !isValidHex(editFormData.color_secundario))
      newErrors.color_secundario = 'Color secundario inválido (formato hex, ej: #1a73e8)';
    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveClient = async () => {
    if (!validateEditForm()) return;
    try {
      setSavingClient(true);
      const token      = localStorage.getItem('token');
      const clientData = {
        razon_social:     editFormData.razon_social,
        alias:            editFormData.alias,
        cif:              editFormData.cif,
        calle:            editFormData.calle,
        localidad:        editFormData.localidad,
        provincia:        editFormData.provincia,
        // String, NO parseInt: conserva ceros a la izquierda (p. ej. "01234").
        c_postal:         String(editFormData.c_postal || '').trim(),
        telefono1:        editFormData.telefono1,
        telefono2:        editFormData.telefono2,
        mail:             editFormData.mail,
        mail_admin:       editFormData.mail_admin,
        mail_admin_2:     editFormData.mail_admin_2,
        fax:              editFormData.fax,
        contacto:         editFormData.contacto,
        clinica_matriz:   editingClient.clinica_matriz || 0,
        cuenta_contable:  editFormData.cuenta_contable,
        dia_pago:         editFormData.dia_pago,
        comision:         parseFloat(editFormData.comision) || 0,
        modelo_negocio:   editFormData.modelo_negocio,
        modneg_detalle:   editFormData.modneg_detalle,
        ahorro:           parseFloat(editFormData.ahorro) || 0,
        iban:             editFormData.iban,
        email_calendario: editFormData.email_calendario,
        tipo_calendario:  editFormData.tipo_calendario,
        color_primario:   editFormData.color_primario  || '#3cbf9a',
        color_secundario: editFormData.color_secundario || '#1a73e8',
        logo_filename:    editFormData.logo_filename   || null,   // ← compatibilidad
        logo_archivo_id:  editFormData.logo_archivo_id || null,   // ← logo en BD
        gestion_stock:    editFormData.gestion_stock || 'inactivo',
        pedidos_pendientes: !!editFormData.pedidos_pendientes,
        gestion_albaranes: !!editFormData.gestion_albaranes,
        notif_incidencias: !!editFormData.notif_incidencias,
      };
      const response = await fetch(`${API_URL}/api/clientes/${editingClient.id}`, {
        method:  'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify(clientData),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al actualizar el cliente');
      }
      const data = await response.json();
      setActiveClient(data.cliente);
      sessionStorage.setItem('activeClient', JSON.stringify(data.cliente));
      setShowEditClientModal(false);
      setEditingClient(null);
      alert('Cliente actualizado correctamente');
    } catch (error) {
      console.error('Error al actualizar cliente:', error);
      alert(error.message || 'Error al actualizar el cliente');
    } finally {
      setSavingClient(false);
    }
  };

  // ── Subida de Archivos Excel ───────────────────────────────────────────────
  const handleFileUpload = async (file) => {
    if (!activeClient) { setUploadError('Por favor seleccione un cliente antes de subir archivos'); return; }
    if (!file) { setUploadError('No se ha seleccionado ningún archivo'); return; }
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!validTypes.includes(file.type)) { setUploadError('Por favor seleccione un archivo Excel (.xlsx o .xls)'); return; }
    setUploadingFile(true);
    setUploadError('');
    setUploadMessage('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token    = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clientes/${activeClient.id}/upload-excel`, {
        method:  'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body:    formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al procesar el archivo');
      setUploadMessage(
        `✅ Archivo procesado correctamente. \nProductos procesados: ${data.processedCount}` +
        (data.errorCount > 0 ? `\nErrores: ${data.errorCount}` : ''),
      );
      if (data.errors?.length > 0) console.warn('Errores durante el procesamiento:', data.errors);
    } catch (error) {
      console.error('Error al subir archivo:', error);
      setUploadError(error.message || 'Error al procesar el archivo');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDragOver  = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop      = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleFileUpload(files[0]);
  };
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) handleFileUpload(file);
    e.target.value = '';
  };

  // ── Carga y Búsqueda ───────────────────────────────────────────────────────
  const loadClientData = async (clientId) => {
    try {
      setLoading(true);
      const token    = localStorage.getItem('token');
      if (!token) throw new Error('No hay token de autenticación');
      const response = await fetch(`${API_URL}/api/clientes/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al obtener datos del cliente');
      const data   = await response.json();
      const client = data.cliente;
      setActiveClient(client);
      sessionStorage.setItem('activeClient', JSON.stringify(client));
      fetchLinkedClients(client.id, client.clinica_matriz);
      fetchClientProviders(client.id);
    } catch (error) {
      console.error('Error al cargar cliente:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClientSearch = async (query) => {
    if (!isAdmin) return;
    setClientSearchInput(query);
    if (query.length < 3) { setSearchResults([]); setSearching(false); return; }
    try {
      setSearching(true);
      const token    = localStorage.getItem('token');
      if (!token) throw new Error('No hay token de autenticación');
      const response = await fetch(
        `${API_URL}/api/clientes/search?query=${encodeURIComponent(query)}`,
        { headers: { 'Authorization': `Bearer ${token}` } },
      );
      if (!response.ok) throw new Error('Error al buscar clientes');
      const data = await response.json();
      setSearchResults(data.clientes);
    } catch (error) {
      console.error('Error en la búsqueda:', error);
      setError(error.message);
    } finally {
      setSearching(false);
    }
  };

  const fetchLinkedClients = async (clientId, matrixId) => {
    try {
      setLoading(true);
      const token          = localStorage.getItem('token');
      if (!token) throw new Error('No hay token de autenticación');
      const matrixToSearch = matrixId > 0 ? matrixId : clientId;
      const response       = await fetch(`${API_URL}/api/clientes/matriz/${matrixToSearch}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al obtener clientes vinculados');
      const data = await response.json();
      setLinkedClients(data.clientes.filter(client => client.id !== clientId));
    } catch (error) {
      console.error('Error al cargar clientes vinculados:', error);
      setLinkedClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectClient = async (client) => {
    setActiveClient(client);
    if (isAdmin) { setSearchResults([]); setClientSearchInput(''); }
    sessionStorage.setItem('activeClient', JSON.stringify(client));
    fetchLinkedClients(client.id, client.clinica_matriz);
    fetchClientProviders(client.id);
  };

  const fetchClientProviders = async (clientId) => {
    try {
      const token    = localStorage.getItem('token');
      if (!token) throw new Error('No hay token de autenticación');
      const response = await fetch(`${API_URL}/api/clientes/${clientId}/proveedores`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al obtener los proveedores');
      const data = await response.json();
      setProviders(data.proveedores);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
      setProviders([]);
    }
  };

  const handleProviderSearch = async (query) => {
    setProviderSearchInput(query);
    if (query.length < 3) { setProviderSearchResults([]); return; }
    try {
      const token    = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/proveedores?search=${query}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al buscar proveedores');
      const data = await response.json();
      setProviderSearchResults(data.proveedores);
    } catch (error) {
      console.error('Error al buscar proveedores:', error);
    }
  };

  const handleAddProvider = async (providerId) => {
    try {
      const token    = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clientes/${activeClient.id}/proveedores`, {
        method:  'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ proveedor_id: providerId }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Error al añadir el proveedor');
      }
      alert('Proveedor añadido correctamente');
      setShowProviderSearch(false);
      setProviderSearchInput('');
      setProviderSearchResults([]);
      fetchClientProviders(activeClient.id);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleProviderFieldChange = (providerId, field, value) => {
    setEditedProviders(prev => ({
      ...prev,
      [providerId]: { ...(prev[providerId] || {}), [field]: value },
    }));
  };

  const getProviderFieldValue = (provider, field) => {
    if (editedProviders[provider.id] && editedProviders[provider.id][field] !== undefined) {
      return editedProviders[provider.id][field];
    }
    return provider[field] || '';
  };

  const handleSaveProvider = async (provider) => {
    const edited = editedProviders[provider.id] || {};
    const payload = {
      nombre:    edited.nombre    !== undefined ? edited.nombre    : provider.nombre,
      direccion: edited.direccion !== undefined ? edited.direccion : provider.direccion,
      telefono:  edited.telefono  !== undefined ? edited.telefono  : provider.telefono,
      email:     edited.email     !== undefined ? edited.email     : provider.email,
      precio_min: edited.precio_min !== undefined ? edited.precio_min : provider.precio_min,
    };

    const correoComercial = edited.correo_comercial !== undefined ? edited.correo_comercial : provider.correo_comercial;

    // OJO: `alias_proveedor` NO va dentro de `payload`. `payload` viaja a
    // PUT /api/proveedores/:id, que actualiza el proveedor de forma GLOBAL para
    // todos los clientes. El alias es del VÍNCULO cliente↔proveedor y se guarda
    // en su propia ruta, igual que el correo comercial.
    const aliasProveedor = edited.alias_proveedor !== undefined ? edited.alias_proveedor : provider.alias_proveedor;

    try {
      const token = localStorage.getItem('token');

      // Guardar datos globales del proveedor
      const res1 = await fetch(`${API_URL}/api/proveedores/${provider.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res1.ok) throw new Error('Error al guardar datos del proveedor');

      // Guardar correo comercial (relación cliente-proveedor)
      const res2 = await fetch(`${API_URL}/api/clientes/${activeClient.id}/proveedores/${provider.id}/correo-comercial`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ correo_comercial: correoComercial || '' }),
      });
      if (!res2.ok) throw new Error('Error al guardar el correo comercial');

      // Guardar el nombre visible para esta clínica (relación cliente-proveedor).
      // Vacío → el backend lo normaliza a NULL y la clínica vuelve a ver el real.
      const res3 = await fetch(`${API_URL}/api/clientes/${activeClient.id}/proveedores/${provider.id}/alias`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ alias_proveedor: aliasProveedor || '' }),
      });
      if (!res3.ok) throw new Error('Error al guardar el nombre visible para esta clínica');

      alert('Proveedor guardado correctamente');
      setEditedProviders(prev => { const next = { ...prev }; delete next[provider.id]; return next; });
      fetchClientProviders(activeClient.id);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleClearActiveClient = () => {
    if (!isAdmin) return;
    setActiveClient(null);
    setLinkedClients([]);
    sessionStorage.removeItem('activeClient');
    setShowClientsList(true);
  };

  const handleSincronizarMatriz = async () => {
    if (!activeClient || !activeClient.clinica_matriz) return;
    const matrizNombre = activeClient.clinica_matriz_nombre
      ? `"${activeClient.clinica_matriz_nombre}"`
      : `ID ${activeClient.clinica_matriz}`;
    const confirmar = window.confirm(
      `Se van a añadir a "${activeClient.razon_social}" los proveedores y precios ` +
      `que tenga la clínica matriz ${matrizNombre} y a este cliente le falten.\n\n` +
      `No se borrará ni modificará nada de lo que el cliente ya tenga.\n\n¿Continuar?`
    );
    if (!confirmar) return;

    try {
      setSyncingMatriz(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/clientes/${activeClient.id}/sincronizar-matriz`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Error al sincronizar con la clínica matriz');
      }
      const { proveedores_anadidos = 0, precios_anadidos = 0 } = data;
      if (proveedores_anadidos === 0 && precios_anadidos === 0) {
        alert('El cliente ya está al día con su clínica matriz: no había nada nuevo que copiar.');
      } else {
        alert(
          `Sincronización completada:\n` +
          `• ${proveedores_anadidos} proveedor(es) añadido(s)\n` +
          `• ${precios_anadidos} precio(s) añadido(s)`
        );
      }
      fetchClientProviders(activeClient.id);
    } catch (error) {
      console.error('Error al sincronizar con clínica matriz:', error);
      alert(error.message || 'Error al sincronizar con la clínica matriz');
    } finally {
      setSyncingMatriz(false);
    }
  };

  const handleBackToList = () => setShowClientsList(true);

  // ── Utilidades ─────────────────────────────────────────────────────────────
  const getProvincias = () => [
    "Álava","Albacete","Alicante","Almería","Asturias","Ávila","Badajoz","Barcelona",
    "Burgos","Cáceres","Cádiz","Cantabria","Castellón","Ciudad Real","Córdoba","Cuenca",
    "Gerona","Granada","Guadalajara","Guipúzcoa","Huelva","Huesca","Islas Baleares",
    "Jaén","La Coruña","La Rioja","Las Palmas","León","Lérida","Lugo","Madrid","Málaga",
    "Murcia","Navarra","Orense","Palencia","Pontevedra","Salamanca","Santa Cruz de Tenerife",
    "Segovia","Sevilla","Soria","Tarragona","Teruel","Toledo","Valencia","Valladolid",
    "Vizcaya","Zamora","Zaragoza",
  ];

  const getModelosNegocio = () => [
    { value: 'FIJO_180',              label: 'FIJO 180€ + IVA' },
    { value: 'FIJO_40',               label: 'FIJO 40€ + IVA' },
    { value: 'FIJO_100',              label: 'FIJO 100€ + IVA' },
    { value: 'FIJO_300',              label: 'FIJO 300€ + IVA' },
    { value: 'COMISION_TRANSACCIONES',label: 'COMISION TRANSACCIONES' },
    { value: 'COMISION_AHORRO',       label: 'COMISION AHORRO' },
    { value: 'ESCALADO',              label: 'ESCALADO' },
  ];

  const getTiposCalendario = () => [
    { value: 'ninguno',  label: 'Sin sincronización' },
    { value: 'google',   label: 'Google Calendar' },
    { value: 'outlook',  label: 'Outlook Calendar' },
    { value: 'icloud',   label: 'iCloud Calendar' },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="fixed inset-0 top-16 bg-white flex justify-center items-center z-50">
        <div className="text-center">
          <div className="relative mb-8">
            <div className="w-20 h-20 border-4 rounded-full animate-spin mx-auto"
              style={{ borderColor: theme.primaryBorder, borderTopColor: theme.primary }}></div>
            <Building2 className="w-8 h-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              style={textPrimary} />
          </div>
          <h3 className="text-xl font-bold mb-2" style={textPrimary}>Cargando información</h3>
          <p className="text-gray-600">Preparando gestión de clientes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 top-16 bg-white flex justify-center items-center z-40">
        <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg max-w-md">
          <h3 className="font-medium text-lg mb-2">Error al cargar los datos</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-x-0 top-16 bottom-0 bg-gray-50 flex overflow-hidden">

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <aside className="w-80 bg-white shadow-lg border-r border-gray-200 flex-shrink-0 overflow-y-auto">
        <div className="p-6">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 flex items-center">
              <Building2 className="mr-3" size={24} style={textPrimary} />
              {isAdmin ? 'Gestión de Clientes' : 'Mi Centro'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {isAdmin ? 'Seleccione un cliente para continuar' : 'Información del centro médico'}
            </p>
          </div>

          {isAdmin && (
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Buscar Cliente</label>
              <SearchInput
                size="lg"
                value={clientSearchInput}
                onChange={(e) => handleClientSearch(e.target.value)}
                placeholder="Razón social del cliente..."
              />
              {searching && (
                <div className="mt-3 text-center text-sm text-gray-500 py-4">
                  <div className="animate-pulse">Buscando clientes...</div>
                </div>
              )}
              {!searching && searchResults.length > 0 && (
                <div className="mt-3 max-h-64 overflow-y-auto bg-white rounded-lg shadow-sm border border-gray-200">
                  <ul className="divide-y divide-gray-100">
                    {searchResults.map(client => (
                      <li key={client.id} className="hover:bg-green-50 transition-colors duration-150">
                        <button onClick={() => handleSelectClient(client)} className="w-full text-left px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900">{client.razon_social}</div>
                          {client.alias && <div className="text-xs text-gray-500 mt-1">Alias: {client.alias}</div>}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!searching && clientSearchInput.length >= 3 && searchResults.length === 0 && (
                <div className="mt-3 text-center text-sm text-gray-500 py-4 bg-gray-50 rounded-lg">
                  <Search size={24} className="mx-auto text-gray-400 mb-2" />
                  No se encontraron clientes
                </div>
              )}
            </div>
          )}

          {activeClient && (
            <div className="space-y-4">
              {isClient && (
                <div>
                  <h3 className="text-white px-4 py-3 rounded-t-lg font-semibold flex items-center"
                    style={bgPrimary}>
                    <Building2 size={18} className="mr-2" />
                    Mi Centro Principal
                  </h3>
                  <div className="rounded-b-lg border border-gray-200" style={bgPrimaryLight}>
                    <div className="p-4 cursor-pointer transition-colors duration-150"
                      style={activeClient.id === user.clinica_matriz ? { borderLeft: `4px solid ${theme.primary}` } : {}}>
                      <button onClick={() => user.clinica_matriz && loadClientData(user.clinica_matriz)} className="w-full text-left">
                        <div className="font-semibold text-gray-800">
                          {activeClient.id === user.clinica_matriz ? activeClient.razon_social : 'Volver al centro principal'}
                        </div>
                        {activeClient.id === user.clinica_matriz && (
                          <div className="text-sm mt-1" style={textPrimaryDark}>✓ Centro activo</div>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {linkedClients.length > 0 && (
                <div className={isClient ? 'mt-4' : ''}>
                  <div className="flex items-center justify-between mb-3 px-1">
                    <div className="flex items-center text-gray-700">
                      <Users size={16} className="mr-2 text-gray-500" />
                      <h3 className="font-semibold text-sm">Centros Vinculados</h3>
                    </div>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      {linkedClients.length}
                    </span>
                  </div>
                  <div className="max-h-64 overflow-y-auto bg-white rounded-lg border border-gray-200">
                    <ul className="divide-y divide-gray-100">
                      {linkedClients.map(client => (
                        <li key={client.id} className="transition-colors duration-150"
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.primaryLight)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}>
                          <button onClick={() => handleSelectClient(client)} className="w-full text-left p-4">
                            <div className="font-medium text-gray-900">{client.razon_social}</div>
                            {client.alias && <div className="text-sm text-gray-500 mt-1">{client.alias}</div>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* ── Main Content ─────────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          {activeClient ? (
            <div className="flex-1 flex flex-col min-h-0">

              {/* Header del cliente (estilo vista Cliente) */}
              <div className="flex-shrink-0 bg-white shadow-sm border-b border-gray-200 p-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center space-x-4 min-w-0">
                    <div className="p-3 rounded-full flex-shrink-0" style={bgPrimaryLight}>
                      <Building2 className="w-8 h-8" style={textPrimary} />
                    </div>
                    <div className="min-w-0">
                      <h1 className="text-2xl font-bold text-gray-900 truncate">{activeClient.razon_social}</h1>
                      {activeClient.alias && <p className="text-gray-600 truncate">{activeClient.alias}</p>}
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-500">
                        {activeClient.cif && (
                          <div className="flex items-center"><CreditCard className="w-4 h-4 mr-1" />CIF: {activeClient.cif}</div>
                        )}
                        {activeClient.telefono1 && (
                          <div className="flex items-center"><Phone className="w-4 h-4 mr-1" />{activeClient.telefono1}</div>
                        )}
                        {activeClient.mail && (
                          <div className="flex items-center"><Mail className="w-4 h-4 mr-1" /><span className="truncate">{activeClient.mail}</span></div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={abrirCatalogoModal} disabled={loadingCatalogo}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center transition-colors disabled:opacity-60">
                      {loadingCatalogo
                        ? <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2" />
                        : <Download className="w-4 h-4 mr-2" />}
                      Catálogo
                    </button>
                    {isAdmin && activeClient.clinica_matriz > 0 && (
                      <button onClick={handleSincronizarMatriz} disabled={syncingMatriz}
                        className="p-2 border border-gray-300 text-gray-500 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60"
                        title="Sincronizar con la clínica matriz (añade proveedores y precios que falten)">
                        <RefreshCw size={18} className={syncingMatriz ? 'animate-spin' : ''} />
                      </button>
                    )}
                    <button onClick={handleOpenTokenModal}
                      className="p-2 border border-gray-300 text-gray-500 rounded-lg hover:bg-gray-50 transition-colors"
                      title="Gestionar token de calendario público"><Link size={18} /></button>
                    <button onClick={handleEditClient}
                      className="p-2 border border-gray-300 text-gray-500 rounded-lg hover:bg-gray-50 transition-colors"
                      title="Editar cliente"><Edit size={18} /></button>
                    {isAdmin && (
                      <button onClick={handleClearActiveClient}
                        className="p-2 border border-gray-300 text-gray-500 rounded-lg hover:bg-gray-50 hover:text-red-600 hover:border-red-200 transition-colors"
                        title="Deseleccionar cliente"><X size={18} /></button>
                    )}
                  </div>
                </div>
              </div>

              {/* Contenido */}
              <div className="flex-1 overflow-auto p-6 space-y-8">
                {/* Sección: Gestión */}
                <div>
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                    <Package size={16} className="mr-2 text-gray-400" />Gestión
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { Icon: ShoppingCart, titulo: 'Nuevo Pedido', desc: 'Crear un pedido personalizado', accion: 'Crear Pedido', onClick: handleNuevoPedido, iconBg: 'bg-green-100', iconText: 'text-green-600', btn: 'bg-green-600 hover:bg-green-700' },
                      { Icon: PlusCircle, titulo: 'Añadir Producto', desc: 'Gestionar productos manualmente', accion: 'Añadir', onClick: handleAddProduct, iconBg: 'bg-blue-100', iconText: 'text-blue-600', btn: 'bg-blue-600 hover:bg-blue-700' },
                      { Icon: Edit, titulo: 'Actualizar', desc: 'Modificar productos existentes', accion: 'Actualizar', onClick: handleUpdateProduct, iconBg: 'bg-purple-100', iconText: 'text-purple-600', btn: 'bg-purple-600 hover:bg-purple-700' },
                      { Icon: Users, titulo: 'Proveedores', desc: providers.length > 0 ? `${providers.length} asociados` : 'Gestionar proveedores', accion: 'Gestionar', onClick: () => { fetchClientProviders(activeClient.id); setShowProviderModal(true); }, theme: true },
                      { Icon: CreditCard, titulo: 'Límites de pedido', desc: 'Rango de importe por usuario', accion: 'Gestionar', onClick: () => { fetchLimites(activeClient.id); setShowLimitesModal(true); }, theme: true },
                    ].map(a => (
                      <div key={a.titulo} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all flex flex-col">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`p-2.5 rounded-lg flex-shrink-0 ${a.theme ? '' : a.iconBg}`} style={a.theme ? bgPrimaryLight : undefined}>
                            <a.Icon size={20} className={a.theme ? '' : a.iconText} style={a.theme ? textPrimary : undefined} />
                          </div>
                          <h3 className="font-semibold text-gray-800">{a.titulo}</h3>
                        </div>
                        <p className="text-sm text-gray-500 mb-4 flex-1">{a.desc}</p>
                        <button onClick={a.onClick} disabled={!activeClient}
                          className={`w-full text-white py-2 px-4 rounded-lg flex items-center justify-center text-sm font-medium transition-colors disabled:bg-gray-400 ${a.theme ? '' : a.btn}`}
                          style={a.theme ? bgPrimary : undefined}
                          onMouseEnter={a.theme ? (e) => (e.currentTarget.style.backgroundColor = theme.primaryDark) : undefined}
                          onMouseLeave={a.theme ? (e) => (e.currentTarget.style.backgroundColor = theme.primary) : undefined}>
                          <a.Icon size={16} className="mr-2" />{a.accion}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sección: Documentos */}
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <div className="p-3 rounded-lg mr-4" style={{ backgroundColor: 'rgba(255,165,0,0.1)' }}>
                      <Upload size={24} className="text-orange-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800">Gestión de Documentos</h3>
                      <p className="text-gray-600">Suba archivos Excel con listas de productos</p>
                    </div>
                  </div>
                  <button onClick={() => setShowExcelHelpModal(true)}
                    className="p-2 rounded-full transform transition-all duration-200 hover:scale-110"
                    style={bgPrimaryLight}
                    title="Ver formato requerido del Excel">
                    <HelpCircle size={24} style={textPrimary} />
                  </button>
                </div>
                <div
                  className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center transform transition-all duration-300 cursor-pointer min-h-[200px] ${
                    isDragOver
                      ? 'scale-105'
                      : uploadingFile
                      ? 'border-yellow-500 bg-yellow-50'
                      : uploadError
                      ? 'border-red-500 bg-red-50'
                      : uploadMessage
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-gray-400'
                  }`}
                  style={isDragOver ? { borderColor: theme.primary, backgroundColor: theme.primaryLight } : {}}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input type="file" id="file-upload" className="hidden" accept=".xlsx,.xls"
                    onChange={handleFileSelect} disabled={uploadingFile} />
                  <label htmlFor="file-upload" className="cursor-pointer w-full h-full flex flex-col items-center justify-center">
                    {uploadingFile ? (
                      <>
                        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-yellow-600 border-opacity-75 mb-4"></div>
                        <div className="text-yellow-700 text-center">
                          <p className="font-semibold text-lg">Procesando archivo...</p>
                          <p className="text-sm">Por favor espere mientras se cargan los datos</p>
                        </div>
                      </>
                    ) : uploadError ? (
                      <>
                        <div className="bg-red-100 p-4 rounded-full mb-4"><X size={32} className="text-red-500" /></div>
                        <div className="text-red-700 text-center">
                          <p className="font-semibold text-lg">Error al procesar</p>
                          <p className="text-sm">{uploadError}</p>
                        </div>
                      </>
                    ) : uploadMessage ? (
                      <>
                        <div className="bg-green-100 p-4 rounded-full mb-4"><FileText size={32} className="text-green-500" /></div>
                        <div className="text-green-700 text-center">
                          <p className="font-semibold text-lg">¡Archivo procesado!</p>
                          <p className="text-sm whitespace-pre-line">{uploadMessage}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="bg-gray-100 p-4 rounded-full mb-4"><Upload size={32} className="text-gray-400" /></div>
                        <div className="text-gray-600 text-center">
                          <p className="font-semibold text-lg">Subir Lista de Productos</p>
                          <p className="text-sm mb-2">Arrastre y suelte un archivo Excel o haga clic para seleccionar</p>
                          <p className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full inline-block">
                            Formatos soportados: .xlsx, .xls
                          </p>
                        </div>
                      </>
                    )}
                  </label>
                </div>
                {(uploadMessage || uploadError) && (
                  <div className="mt-4 text-center">
                    <button onClick={() => { setUploadMessage(''); setUploadError(''); }}
                      className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                      Limpiar mensaje
                    </button>
                  </div>
                )}
              </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-auto p-6">
              {showClientsList ? (
                <div>
                  <div className="mb-6 flex items-center justify-between">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">Gestión de Clientes</h1>
                      <p className="text-gray-600">Administra y selecciona tus clientes</p>
                    </div>
                  </div>
                  <ClientsList onClientSelected={handleClientSelected} />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full py-12">
                  <div className="text-center max-w-2xl">
                    {isAdmin ? (
                      <div className="bg-white rounded-2xl shadow-lg p-12 border border-gray-100">
                        <div className="mb-8">
                          <div className="p-6 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center"
                            style={bgPrimaryLight}>
                            <Search size={48} style={textPrimary} />
                          </div>
                          <h2 className="text-3xl font-bold text-gray-800 mb-4">Bienvenido al Sistema de Gestión</h2>
                          <p className="text-gray-600 text-lg leading-relaxed">
                            Seleccione un cliente para comenzar a gestionar pedidos, productos y proveedores.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white rounded-2xl shadow-lg p-12 border border-gray-100">
                        <div className="mb-8">
                          <div className="p-6 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center"
                            style={bgPrimaryLight}>
                            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-opacity-75"
                              style={borderPrimary}></div>
                          </div>
                          <h2 className="text-3xl font-bold text-gray-800 mb-4">Cargando Centro Médico</h2>
                          <p className="text-gray-600 text-lg leading-relaxed">
                            Estamos preparando la información de su centro médico.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* ════════════════ MODALES ════════════════ */}

      {/* ── Modal Ayuda Excel ────────────────────────────────────────────────── */}
      {showCatalogoModal && (
        <div className="fixed top-16 inset-x-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="text-white p-4 border-b flex-shrink-0" style={bgPrimary}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <Download size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Descargar catálogo</h2>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      Elegí proveedores y categorías, o dejá vacío para incluir todo
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowCatalogoModal(false)}
                  className="p-2 rounded-lg transition-all duration-200 hover:bg-red-500 text-white">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-semibold text-gray-800">Proveedores</h5>
                  <button type="button" className="text-xs text-gray-500 hover:text-gray-700"
                    onClick={() => setSelProvIds([])}>Todos</button>
                </div>
                <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100">
                  {providers.length === 0 && (
                    <p className="text-xs text-gray-400 p-3">Este cliente no tiene proveedores asignados.</p>
                  )}
                  {providers.map(pv => (
                    <label key={pv.id} className="flex items-center gap-2 p-2 text-sm hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" className="accent-teal-500"
                        checked={selProvIds.includes(pv.id)}
                        onChange={(e) => setSelProvIds(prev => e.target.checked ? [...prev, pv.id] : prev.filter(id => id !== pv.id))} />
                      <span className="truncate">{pv.nombre}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-semibold text-gray-800">Categorías</h5>
                  <button type="button" className="text-xs text-gray-500 hover:text-gray-700"
                    onClick={() => setSelCategorias([])}>Todas</button>
                </div>
                <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100">
                  {[
                    { key: 'cirugia',     label: 'Cirugía' },
                    { key: 'medicina',    label: 'Medicina' },
                    { key: 'oficina',     label: 'Oficina' },
                    { key: 'limpieza',    label: 'Limpieza' },
                    { key: 'uniformes',   label: 'Uniformes' },
                    { key: 'informatica', label: 'Informática' },
                  ].map(cat => (
                    <label key={cat.key} className="flex items-center gap-2 p-2 text-sm hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" className="accent-teal-500"
                        checked={selCategorias.includes(cat.key)}
                        onChange={(e) => setSelCategorias(prev => e.target.checked ? [...prev, cat.key] : prev.filter(k => k !== cat.key))} />
                      <span className="truncate">{cat.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex items-center justify-between flex-shrink-0">
              <span className="text-xs text-gray-500">
                {selProvIds.length === 0 ? 'Todos los proveedores' : `${selProvIds.length} proveedor(es)`}
                {' · '}
                {selCategorias.length === 0 ? 'todas las categorías' : `${selCategorias.length} categoría(s)`}
              </span>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowCatalogoModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">
                  Cancelar
                </button>
                <button onClick={() => handleDescargarCatalogo({ proveedores: selProvIds, categorias: selCategorias })}
                  disabled={loadingCatalogo}
                  className="px-4 py-2 text-white rounded-lg text-sm flex items-center disabled:opacity-60"
                  style={bgPrimary}>
                  {loadingCatalogo
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    : <Download className="w-4 h-4 mr-2" />}
                  Descargar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showExcelHelpModal && (
        <div className="fixed top-16 inset-x-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="text-white p-4 border-b flex-shrink-0" style={bgPrimary}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <FileText size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Formato del Archivo Excel</h2>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      Guía para estructurar correctamente su archivo de productos
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowExcelHelpModal(false)}
                  className="p-2 rounded-lg transition-all duration-200 hover:bg-red-500 text-white">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="mb-4">
                <h5 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <div className="w-2 h-2 rounded-full mr-2" style={bgPrimary}></div>
                  Estructura de Columnas (A → I)
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  {[
                    { col: 'A-B', desc: 'Columnas libres',           color: 'bg-gray-300 text-gray-800', bg: 'bg-gray-50' },
                    { col: 'C',   desc: 'Nombre del Producto',       bg: 'bg-green-50', main: true },
                    { col: 'D',   desc: 'Ref. Proveedor',            bg: 'bg-green-50', main: true },
                    { col: 'E',   desc: 'Nombre Proveedor',          bg: 'bg-green-50', main: true },
                    { col: 'F',   desc: 'Marca (Opcional)',           color: 'bg-blue-500 text-white', bg: 'bg-blue-50' },
                    { col: 'G',   desc: 'Unidad Básica (Opcional)',   color: 'bg-blue-500 text-white', bg: 'bg-blue-50' },
                    { col: 'H',   desc: 'Precio sin IVA (ej: 8.50)', bg: 'bg-green-50', main: true },
                    { col: 'I',   desc: 'Precio con IVA (ej: 10.29)',bg: 'bg-green-50', main: true },
                  ].map(item => (
                    <div key={item.col} className={`flex items-center p-2 ${item.bg} rounded`}>
                      <span
                        className={`font-mono px-2 py-1 rounded text-xs mr-3 min-w-[30px] text-center ${item.main ? 'text-white' : item.color}`}
                        style={item.main ? bgPrimary : {}}
                      >
                        {item.col}
                      </span>
                      <span className="font-medium text-xs">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-4 text-center">
                <button onClick={() => setShowExcelHelpModal(false)}
                  className="px-4 py-2 text-white rounded-lg transition-colors duration-200 text-sm"
                  style={bgPrimary}>
                  ¡Entendido!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Token de Calendario ────────────────────────────────────────── */}
      {showTokenModal && (
        <div className="fixed top-16 inset-x-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full flex flex-col" style={{ maxHeight: 'calc(100vh - 6rem)' }}>
            <div className="text-white p-4 border-b flex-shrink-0" style={bgPrimary}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <Link size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Token de Calendario Público</h2>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      Gestionar acceso público al calendario de citas
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowTokenModal(false)}
                  className="p-2 rounded-lg transition-all duration-200 hover:bg-red-500 text-white">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {calendarToken ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Token Actual</label>
                    <div className="flex gap-2">
                      <input type="text" value={calendarToken} readOnly
                        className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono" />
                      <button onClick={() => handleCopyToken(calendarToken)}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center">
                        {copySuccess ? <CheckCircle size={18} /> : <Copy size={18} />}
                      </button>
                    </div>
                    {tokenCreatedAt && (
                      <p className="text-xs text-gray-500 mt-1">
                        Generado: {new Date(tokenCreatedAt).toLocaleString('es-ES')}
                      </p>
                    )}
                  </div>
                  <div className="pt-4 border-t">
                    <button onClick={handleGenerateToken} disabled={generatingToken}
                      className="w-full px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center justify-center">
                      {generatingToken ? (
                        <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>Generando...</>
                      ) : (
                        <><RefreshCw size={18} className="mr-2" />Generar Nuevo Token</>
                      )}
                    </button>
                    <p className="text-xs text-orange-600 mt-2 text-center">
                      ⚠️ Al generar un nuevo token, el anterior dejará de funcionar
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="bg-gray-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <Link size={32} className="text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">No hay token generado</h3>
                  <p className="text-gray-600 mb-6">Genere un token para habilitar el calendario público</p>
                  <button onClick={handleGenerateToken} disabled={generatingToken}
                    className="px-6 py-3 text-white rounded-lg transition-colors flex items-center mx-auto"
                    style={{ backgroundColor: generatingToken ? '#9ca3af' : theme.primary }}>
                    {generatingToken ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>Generando...</>
                    ) : (
                      <><Link size={18} className="mr-2" />Generar Token</>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Límites de pedido por usuario ─────────────────────────────── */}
      {showLimitesModal && (
        <div className="fixed top-16 inset-x-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full flex flex-col" style={{ maxHeight: 'calc(100vh - 6rem)' }}>
            {/* Header */}
            <div className="text-white p-4 border-b flex-shrink-0 rounded-t-2xl" style={bgPrimary}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <CreditCard size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Límites de pedido por usuario</h2>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      {activeClient?.razon_social}
                    </p>
                  </div>
                </div>
                <button onClick={() => { setShowLimitesModal(false); setLimitesError(''); }}
                  className="p-2 rounded-lg transition-all duration-200 hover:bg-red-500 text-white">
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              <p className="text-sm text-gray-500 mb-4">
                Define hasta qué importe (€, IVA incluido) puede pedir cada usuario sin aprobación.
                Si un pedido supera su máximo, pasa al supervisor. Deja el máximo vacío para «sin techo».
                Los rangos de los usuarios no pueden solaparse.
              </p>

              {(limitesError || (!limitesLoading && validarLimites())) && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 flex items-center text-sm">
                  <AlertCircle size={16} className="mr-2 flex-shrink-0" />{limitesError || validarLimites()}
                </div>
              )}

              {limitesLoading ? (
                <div className="py-12 text-center text-gray-400">Cargando usuarios…</div>
              ) : limitesUsuarios.length === 0 ? (
                <div className="py-12 text-center text-gray-400">Esta clínica no tiene usuarios asignados.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-2 pr-4 font-semibold">Usuario</th>
                        <th className="py-2 px-2 font-semibold">Desde (€)</th>
                        <th className="py-2 px-2 font-semibold">Hasta (€)</th>
                        <th className="py-2 px-2 font-semibold">Aprobador</th>
                      </tr>
                    </thead>
                    <tbody>
                      {limitesUsuarios.map((u) => (
                        <tr key={u.id} className="border-b last:border-b-0">
                          <td className="py-2 pr-4">
                            <div className="font-medium text-gray-800">{u.nombrec || u.username}</div>
                            <div className="text-xs text-gray-400">{u.username}</div>
                          </td>
                          <td className="py-2 px-2">
                            <input type="text" inputMode="decimal" value={u.importe_min}
                              onChange={(e) => handleLimiteChange(u.id, 'importe_min', e.target.value)}
                              placeholder="0"
                              className="w-28 px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none"
                              onFocus={(e) => (e.target.style.borderColor = theme.primary)}
                              onBlur={(e) => (e.target.style.borderColor = '#d1d5db')} />
                          </td>
                          <td className="py-2 px-2">
                            <input type="text" inputMode="decimal" value={u.importe_max}
                              onChange={(e) => handleLimiteChange(u.id, 'importe_max', e.target.value)}
                              placeholder="∞ sin techo"
                              className="w-28 px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none"
                              onFocus={(e) => (e.target.style.borderColor = theme.primary)}
                              onBlur={(e) => (e.target.style.borderColor = '#d1d5db')} />
                          </td>
                          <td className="py-2 px-2">
                            <select value={u.aprobador_id || ''}
                              onChange={(e) => handleAprobadorChange(u.id, e.target.value)}
                              className="w-48 px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none bg-white"
                              onFocus={(e) => (e.target.style.borderColor = theme.primary)}
                              onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}>
                              <option value="">— Matriz (por defecto) —</option>
                              {aprobadoresDisponibles.filter((a) => a.id !== u.id).map((a) => (
                                <option key={a.id} value={a.id}>
                                  {(a.nombrec || a.username)}{a.permisos === 1 ? ' (admin)' : ''}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t flex-shrink-0 flex justify-end gap-3">
              <button onClick={() => { setShowLimitesModal(false); setLimitesError(''); }}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button onClick={saveLimites} disabled={limitesSaving || limitesLoading || validarLimites() !== null}
                className="px-4 py-2 rounded-lg text-white flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={bgPrimary}>
                <Save size={16} className="mr-2" />{limitesSaving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Gestión de Proveedores ─────────────────────────────────────── */}
      {showProviderModal && (
        <div className="fixed top-16 inset-x-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full flex flex-col" style={{ maxHeight: 'calc(100vh - 6rem)' }}>
            {/* Header */}
            <div className="text-white p-4 border-b flex-shrink-0 rounded-t-2xl" style={bgPrimary}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <Users size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Gestión de Proveedores</h2>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      {activeClient?.razon_social} - {providers.length} proveedores asociados
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowProviderModal(false); setShowProviderSearch(false); setTableSearchFilter(''); setProviderSearchInput(''); setProviderSearchResults([]); setEditedProviders({}); }}
                  className="p-2 rounded-lg transition-all duration-200 hover:bg-red-500 text-white">
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              {/* Barra de acciones */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <button
                  className={`text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center justify-center ${showProviderSearch ? 'bg-red-500 hover:bg-red-600' : 'hover:opacity-90'}`}
                  style={showProviderSearch ? undefined : bgPrimary}
                  onClick={() => { setShowProviderSearch(!showProviderSearch); setProviderSearchInput(''); setProviderSearchResults([]); setTableSearchFilter(''); }}>
                  {showProviderSearch ? <><X size={16} className="mr-2" />Cancelar</> : <><UserPlus size={16} className="mr-2" />Añadir Proveedor</>}
                </button>
                {!showProviderSearch && (
                  <SearchInput
                    className="flex-1"
                    value={tableSearchFilter}
                    onChange={(e) => setTableSearchFilter(e.target.value)}
                    placeholder="Filtrar proveedores en la tabla..."
                  />
                )}
              </div>

              {/* Panel de búsqueda para añadir proveedor */}
              {showProviderSearch && (
                <div className="mb-4 rounded-lg p-4 border" style={{ backgroundColor: theme.primaryLight, borderColor: theme.primaryBorder }}>
                  <label className="block text-sm font-semibold mb-2" style={textPrimaryDark}>Buscar proveedor para añadir</label>
                  <SearchInput
                    size="lg"
                    value={providerSearchInput}
                    onChange={(e) => handleProviderSearch(e.target.value)}
                    placeholder="Escriba el nombre del proveedor (mín. 3 caracteres)..."
                  />
                  {providerSearchResults.length > 0 && providerSearchInput.length >= 3 && (
                    <div className="mt-3 max-h-48 overflow-y-auto bg-white rounded-lg border border-gray-200 shadow-sm">
                      <ul className="divide-y divide-gray-100">
                        {providerSearchResults.map(prov => (
                          <li key={prov.id}
                            className="py-3 px-4 cursor-pointer transition-colors duration-150 flex items-center justify-between"
                            onClick={() => handleAddProvider(prov.id)}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.primaryLight)}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}>
                            <div>
                              <span className="font-medium text-gray-900 text-sm">{prov.nombre}</span>
                              {prov.email && <span className="text-xs text-gray-500 ml-2">({prov.email})</span>}
                            </div>
                            <div className="flex items-center" style={textPrimary}>
                              <span className="text-xs mr-1">Añadir</span>
                              <Plus size={16} />
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {providerSearchInput.length >= 3 && providerSearchResults.length === 0 && (
                    <p className="mt-3 text-sm text-gray-500 text-center py-2">No se encontraron proveedores</p>
                  )}
                </div>
              )}

              {/* Lista de proveedores asociados */}
              {!showProviderSearch && (
                <>
                  {providers.length > 0 ? (
                    <div className="space-y-3">
                      {providers
                        .filter(p =>
                          !tableSearchFilter ||
                          p.nombre?.toLowerCase().includes(tableSearchFilter.toLowerCase()) ||
                          p.email?.toLowerCase().includes(tableSearchFilter.toLowerCase()) ||
                          p.telefono?.includes(tableSearchFilter) ||
                          p.id.toString().includes(tableSearchFilter)
                        )
                        .map(provider => {
                          const hasChanges = !!editedProviders[provider.id];
                          return (
                            <div key={provider.id} className={`bg-white border rounded-xl p-4 transition-all duration-200 ${hasChanges ? 'border-orange-300 shadow-md' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'}`}>
                              {/* Cabecera del proveedor */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <span className="bg-gray-100 text-gray-600 text-xs font-mono px-2 py-1 rounded">#{provider.id}</span>
                                  <span className="font-semibold text-gray-900">{provider.nombre}</span>
                                  {hasChanges && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Sin guardar</span>}
                                </div>
                                <button
                                  className={`${hasChanges ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 hover:bg-gray-500'} text-white py-1.5 px-4 rounded-lg text-sm transition-colors duration-200 flex items-center`}
                                  onClick={() => handleSaveProvider(provider)}>
                                  <Save size={14} className="mr-1.5" />Guardar
                                </button>
                              </div>
                              {/* Campos editables en grid */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    <Building2 className="w-3 h-3 inline mr-1" />Nombre
                                  </label>
                                  <input type="text" value={getProviderFieldValue(provider, 'nombre')}
                                    onChange={(e) => handleProviderFieldChange(provider.id, 'nombre', e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-400" />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    <MapPin className="w-3 h-3 inline mr-1" />Dirección
                                  </label>
                                  <input type="text" value={getProviderFieldValue(provider, 'direccion')}
                                    onChange={(e) => handleProviderFieldChange(provider.id, 'direccion', e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-400" />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    <Phone className="w-3 h-3 inline mr-1" />Teléfono
                                  </label>
                                  <input type="text" value={getProviderFieldValue(provider, 'telefono')}
                                    onChange={(e) => handleProviderFieldChange(provider.id, 'telefono', e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-400" />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    <Mail className="w-3 h-3 inline mr-1" />Email del Proveedor
                                  </label>
                                  <input type="email" value={getProviderFieldValue(provider, 'email')}
                                    onChange={(e) => handleProviderFieldChange(provider.id, 'email', e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-400" />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    <Calculator className="w-3 h-3 inline mr-1" />Precio Mínimo
                                  </label>
                                  <input type="number" step="0.01" min="0" value={getProviderFieldValue(provider, 'precio_min')}
                                    onChange={(e) => handleProviderFieldChange(provider.id, 'precio_min', e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-400" />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    <Mail className="w-3 h-3 inline mr-1" />Correo Comercial
                                    <span className="text-gray-400 ml-1">(solo este cliente)</span>
                                  </label>
                                  <input type="email" value={getProviderFieldValue(provider, 'correo_comercial')}
                                    onChange={(e) => handleProviderFieldChange(provider.id, 'correo_comercial', e.target.value)}
                                    placeholder="comercial@ejemplo.com"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-400" />
                                </div>
                                {/* Alias del proveedor PARA ESTA CLÍNICA. Ocupa toda
                                    la fila porque necesita el texto de ayuda: es un
                                    campo con consecuencias (cambia lo que ve la
                                    clínica en catálogo, pedidos y PDF) y sin
                                    explicación se presta a malentendidos. */}
                                <div className="sm:col-span-2 lg:col-span-3">
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    <Eye className="w-3 h-3 inline mr-1" />Nombre visible para esta clínica
                                    <span className="text-gray-400 ml-1">(opcional)</span>
                                  </label>
                                  <input type="text" maxLength={255}
                                    value={getProviderFieldValue(provider, 'alias_proveedor')}
                                    onChange={(e) => handleProviderFieldChange(provider.id, 'alias_proveedor', e.target.value)}
                                    placeholder={provider.nombre || 'Nombre real del proveedor'}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-400" />
                                  <p className="text-xs text-gray-400 mt-1">
                                    Si lo rellenas, esta clínica verá ese nombre en lugar del real. Déjalo vacío para mostrar el nombre real.
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="bg-gray-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                        <Users size={32} className="text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">No hay proveedores asociados</h3>
                      <p className="text-sm text-gray-500 mb-4">Añade proveedores para que este cliente pueda realizar pedidos</p>
                      <button
                        className="text-white px-5 py-2.5 rounded-lg transition-colors duration-200 flex items-center mx-auto hover:opacity-90"
                        style={bgPrimary}
                        onClick={() => setShowProviderSearch(true)}>
                        <UserPlus size={16} className="mr-2" />Añadir Primer Proveedor
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Edición de Cliente ─────────────────────────────────────────── */}
      {showEditClientModal && editingClient && (
        <div className="fixed top-16 inset-x-0 bottom-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-4">
          <div
            className="bg-white rounded-2xl shadow-2xl w-full flex flex-col"
            style={{ maxWidth: '1050px', maxHeight: 'calc(100vh - 5.5rem)' }}
          >
            {/* Header */}
            <div className="text-white p-4 border-b flex-shrink-0 rounded-t-2xl" style={bgPrimary}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <Building2 size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Editar Cliente</h2>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>{editingClient.razon_social}</p>
                  </div>
                </div>
                <button onClick={() => setShowEditClientModal(false)}
                  className="p-2 rounded-lg transition-all duration-200 hover:bg-red-500 text-white">
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                {/* ── Información Básica ─────────────────────────────────── */}
                <div className="md:col-span-3">
                  <h3 className="text-base font-bold text-gray-800 mb-3 pb-2 border-b-2" style={borderPrimary}>
                    Información Básica
                  </h3>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">
                    <Building2 className="w-4 h-4 inline mr-1" />Razón Social*
                  </label>
                  <input type="text" value={editFormData.razon_social || ''}
                    onChange={(e) => handleEditInputChange('razon_social', e.target.value)}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none text-sm ${editErrors.razon_social ? 'border-red-500' : 'border-gray-300'}`}
                    onFocus={(e) => !editErrors.razon_social && (e.target.style.borderColor = theme.primary)}
                    onBlur={(e)  => !editErrors.razon_social && (e.target.style.borderColor = '#e5e7eb')} />
                  {editErrors.razon_social && <p className="text-red-500 text-xs mt-1">{editErrors.razon_social}</p>}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">
                    <FileText className="w-4 h-4 inline mr-1" />Alias
                  </label>
                  <input type="text" value={editFormData.alias || ''}
                    onChange={(e) => handleEditInputChange('alias', e.target.value)}
                    className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none text-sm border-gray-300"
                    onFocus={(e) => (e.target.style.borderColor = theme.primary)}
                    onBlur={(e)  => (e.target.style.borderColor = '#e5e7eb')} />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">
                    <FileText className="w-4 h-4 inline mr-1" />CIF*
                  </label>
                  <input type="text" value={editFormData.cif || ''}
                    onChange={(e) => handleEditInputChange('cif', e.target.value)}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none text-sm ${editErrors.cif ? 'border-red-500' : 'border-gray-300'}`}
                    onFocus={(e) => !editErrors.cif && (e.target.style.borderColor = theme.primary)}
                    onBlur={(e)  => !editErrors.cif && (e.target.style.borderColor = '#e5e7eb')} />
                  {editErrors.cif && <p className="text-red-500 text-xs mt-1">{editErrors.cif}</p>}
                </div>

                {/* ── Dirección ───────────────────────────────────────────── */}
                <div className="md:col-span-3 mt-2">
                  <h3 className="text-base font-bold text-gray-800 mb-3 pb-2 border-b-2" style={borderPrimary}>
                    Dirección
                  </h3>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-1"><MapPin className="w-4 h-4 inline mr-1" />Calle*</label>
                  <input type="text" value={editFormData.calle || ''}
                    onChange={(e) => handleEditInputChange('calle', e.target.value)}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none text-sm ${editErrors.calle ? 'border-red-500' : 'border-gray-300'}`}
                    onFocus={(e) => !editErrors.calle && (e.target.style.borderColor = theme.primary)}
                    onBlur={(e)  => !editErrors.calle && (e.target.style.borderColor = '#e5e7eb')} />
                  {editErrors.calle && <p className="text-red-500 text-xs mt-1">{editErrors.calle}</p>}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1"><Hash className="w-4 h-4 inline mr-1" />Código Postal*</label>
                  <input type="text" maxLength="5" value={editFormData.c_postal || ''}
                    onChange={(e) => handleEditInputChange('c_postal', e.target.value)}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none text-sm ${editErrors.c_postal ? 'border-red-500' : 'border-gray-300'}`}
                    onFocus={(e) => !editErrors.c_postal && (e.target.style.borderColor = theme.primary)}
                    onBlur={(e)  => !editErrors.c_postal && (e.target.style.borderColor = '#e5e7eb')} />
                  {editErrors.c_postal && <p className="text-red-500 text-xs mt-1">{editErrors.c_postal}</p>}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1"><MapPin className="w-4 h-4 inline mr-1" />Localidad*</label>
                  <input type="text" value={editFormData.localidad || ''}
                    onChange={(e) => handleEditInputChange('localidad', e.target.value)}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none text-sm ${editErrors.localidad ? 'border-red-500' : 'border-gray-300'}`}
                    onFocus={(e) => !editErrors.localidad && (e.target.style.borderColor = theme.primary)}
                    onBlur={(e)  => !editErrors.localidad && (e.target.style.borderColor = '#e5e7eb')} />
                  {editErrors.localidad && <p className="text-red-500 text-xs mt-1">{editErrors.localidad}</p>}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1"><Globe className="w-4 h-4 inline mr-1" />Provincia</label>
                  <select value={editFormData.provincia || ''} onChange={(e) => handleEditInputChange('provincia', e.target.value)}
                    className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none bg-white text-sm border-gray-300"
                    onFocus={(e) => (e.target.style.borderColor = theme.primary)}
                    onBlur={(e)  => (e.target.style.borderColor = '#e5e7eb')}>
                    <option value="">Seleccionar provincia</option>
                    {getProvincias().map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>

                {/* ── Contacto ────────────────────────────────────────────── */}
                <div className="md:col-span-3 mt-2">
                  <h3 className="text-base font-bold text-gray-800 mb-3 pb-2 border-b-2" style={borderPrimary}>
                    Información de Contacto
                  </h3>
                </div>

                {[
                  { field: 'telefono1',    label: 'Teléfono 1',        type: 'tel',   icon: <Phone className="w-4 h-4 inline mr-1" /> },
                  { field: 'telefono2',    label: 'Teléfono 2',        type: 'tel',   icon: <Phone className="w-4 h-4 inline mr-1" /> },
                  { field: 'mail',         label: 'Email',             type: 'email', icon: <Mail  className="w-4 h-4 inline mr-1" />, errorKey: 'mail' },
                  { field: 'mail_admin',   label: 'Email Admin',       type: 'email', icon: <Mail  className="w-4 h-4 inline mr-1" /> },
                  { field: 'mail_admin_2', label: 'Email Admin 2',     type: 'email', icon: <Mail  className="w-4 h-4 inline mr-1" /> },
                  { field: 'contacto',     label: 'Persona de Contacto',type: 'text', icon: <User  className="w-4 h-4 inline mr-1" /> },
                ].map(({ field, label, type, icon, errorKey }) => (
                  <div key={field}>
                    <label className="block text-sm font-bold text-gray-700 mb-1">{icon}{label}</label>
                    <input type={type} value={editFormData[field] || ''}
                      onChange={(e) => handleEditInputChange(field, e.target.value)}
                      className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none text-sm ${errorKey && editErrors[errorKey] ? 'border-red-500' : 'border-gray-300'}`}
                      onFocus={(e) => !(errorKey && editErrors[errorKey]) && (e.target.style.borderColor = theme.primary)}
                      onBlur={(e)  => !(errorKey && editErrors[errorKey]) && (e.target.style.borderColor = '#e5e7eb')} />
                    {errorKey && editErrors[errorKey] && <p className="text-red-500 text-xs mt-1">{editErrors[errorKey]}</p>}
                  </div>
                ))}

                {/* ── Sincronización de Calendario ────────────────────────── */}
                <div className="md:col-span-3 mt-2">
                  <h3 className="text-base font-bold text-gray-800 mb-3 pb-2 border-b-2" style={borderPrimary}>
                    Sincronización de Calendario
                  </h3>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />Tipo de Calendario
                  </label>
                  <select value={editFormData.tipo_calendario || 'ninguno'}
                    onChange={(e) => handleEditInputChange('tipo_calendario', e.target.value)}
                    className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none bg-white text-sm border-gray-300"
                    onFocus={(e) => (e.target.style.borderColor = theme.primary)}
                    onBlur={(e)  => (e.target.style.borderColor = '#e5e7eb')}>
                    {getTiposCalendario().map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-1">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email de Calendario {editFormData.tipo_calendario !== 'ninguno' && '*'}
                  </label>
                  <input type="email" value={editFormData.email_calendario || ''}
                    onChange={(e) => handleEditInputChange('email_calendario', e.target.value)}
                    placeholder={
                      editFormData.tipo_calendario === 'google'  ? 'ejemplo@gmail.com'   :
                      editFormData.tipo_calendario === 'outlook' ? 'ejemplo@outlook.com' :
                      editFormData.tipo_calendario === 'icloud'  ? 'ejemplo@icloud.com'  :
                      'Email del calendario'
                    }
                    disabled={editFormData.tipo_calendario === 'ninguno'}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none text-sm ${
                      editErrors.email_calendario ? 'border-red-500' : 'border-gray-300'
                    } ${editFormData.tipo_calendario === 'ninguno' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    onFocus={(e) =>
                      !editErrors.email_calendario && editFormData.tipo_calendario !== 'ninguno' &&
                      (e.target.style.borderColor = theme.primary)
                    }
                    onBlur={(e) => !editErrors.email_calendario && (e.target.style.borderColor = '#e5e7eb')} />
                  {editErrors.email_calendario && <p className="text-red-500 text-xs mt-1">{editErrors.email_calendario}</p>}
                </div>

                {/* ── Gestión de Stock ──────────────────────────────────────── */}
                <div className="md:col-span-3 mt-2">
                  <h3 className="text-base font-bold text-gray-800 mb-3 pb-2 border-b-2" style={borderPrimary}>
                    Gestión de Stock
                  </h3>
                  <label className="block text-sm font-bold text-gray-800 mb-2">Modo de gestión</label>
                  <select
                    value={editFormData.gestion_stock || 'inactivo'}
                    onChange={(e) => handleEditInputChange('gestion_stock', e.target.value)}
                    className="w-full p-3 rounded-lg border-2 border-gray-200 focus:outline-none text-sm"
                  >
                    <option value="inactivo">Inactivo — sin gestión de stock</option>
                    <option value="albaran">Por albarán — añade productos bajo mínimo automáticamente a los pedidos</option>
                    <option value="picking">Por escaneo — entradas y salidas con código de barras desde la aplicación móvil</option>
                    <option value="mixto">Mixto — albarán con doble verificación (el cliente acepta antes de aplicar al stock) o escaneo cuando no hay albarán</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    <span className="font-semibold">Albarán:</span> el cliente lleva inventario y cuando una unidad
                    cae bajo el mínimo, aparece automáticamente al hacer un nuevo pedido.
                    <br />
                    <span className="font-semibold">Escaneo:</span> las entradas y salidas se hacen con código de barras desde la aplicación móvil,
                    con control de lotes y fechas de caducidad. La salida sigue el orden FEFO (primero los lotes que caducan antes).
                  </p>

                  {/* ── API de Stock (token de integración externa) ─────
                       Siempre visible durante la edición (cuando ya existe el
                       cliente). El propio componente avisa si el modo es
                       'inactivo' o si todavía no hay token emitido. */}
                  {activeClient?.id && (
                    <StockApiTokenSection
                      clienteId={activeClient.id}
                      modoStock={editFormData.gestion_stock || 'inactivo'}
                    />
                  )}
                </div>

                {/* ── Aprobación de pedidos ─────────────────────────────────── */}
                <div className="md:col-span-3 mt-2">
                  <h3 className="text-base font-bold text-gray-800 mb-3 pb-2 border-b-2" style={borderPrimary}>
                    Aprobación de pedidos
                  </h3>
                  <label className="flex items-start gap-3 p-3 rounded-lg border-2 border-gray-200 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={!!editFormData.pedidos_pendientes}
                      onChange={(e) => handleEditInputChange('pedidos_pendientes', e.target.checked)}
                      className="mt-0.5 w-4 h-4"
                      style={{ accentColor: theme.primary }}
                    />
                    <span className="text-sm text-gray-700">
                      <span className="font-bold text-gray-800">Pedidos pendientes de aprobación</span>
                      <br />
                      Si se activa en una <strong>clínica matriz</strong>, los pedidos de sus clínicas hijas
                      pasarán por validación: la matriz los aprobará desde su pestaña «Pendientes de aprobación».
                      Solo tiene efecto en clínicas matriz (en las hijas se ignora).
                    </span>
                  </label>

                  <label className="flex items-start gap-3 p-3 mt-3 rounded-lg border-2 border-gray-200 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={!!editFormData.gestion_albaranes}
                      onChange={(e) => handleEditInputChange('gestion_albaranes', e.target.checked)}
                      className="mt-0.5 w-4 h-4"
                      style={{ accentColor: theme.primary }}
                    />
                    <span className="text-sm text-gray-700">
                      <span className="font-bold text-gray-800">Gestión de albaranes</span>
                      <br />
                      Si se activa, el cliente verá la pestaña de albaranes con el listado de pedidos
                      recibidos y la descarga en .zip. Si se activa en una <strong>clínica matriz</strong>,
                      todas sus clínicas hijas la tendrán activada automáticamente.
                    </span>
                  </label>

                  <label className="flex items-start gap-3 p-3 mt-3 rounded-lg border-2 border-gray-200 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={!!editFormData.notif_incidencias}
                      onChange={(e) => handleEditInputChange('notif_incidencias', e.target.checked)}
                      className="mt-0.5 w-4 h-4"
                      style={{ accentColor: theme.primary }}
                    />
                    <span className="text-sm text-gray-700">
                      <span className="font-bold text-gray-800">Notificaciones de incidencias por email</span>
                      <br />
                      Si se activa, al registrar una incidencia se envía un aviso por email al equipo interno
                      (soporte) y a la <strong>clínica matriz</strong>. Desactívalo para silenciar esos correos
                      en este cliente.
                    </span>
                  </label>
                </div>

                {/* ── Personalización Visual ──────────────────────────────── */}
                <div className="md:col-span-3 mt-2">
                  <h3 className="text-base font-bold text-gray-800 mb-1 pb-2 border-b-2 flex items-center" style={borderPrimary}>
                    <Palette className="w-5 h-5 mr-2" style={textPrimary} />
                    Personalización Visual
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Configura los colores y el logo de la interfaz para este cliente y sus centros asociados.
                  </p>
                </div>

                {/* Color Primario */}
                <div>
                  <ColorPickerField
                    label="Color Primario"
                    value={editFormData.color_primario || '#3cbf9a'}
                    defaultColor="#3cbf9a"
                    onChange={(v) => handleEditInputChange('color_primario', v)}
                    helpText="Botones principales, cabeceras y elementos activos"
                  />
                  {editErrors.color_primario && <p className="text-red-500 text-xs mt-1">{editErrors.color_primario}</p>}
                </div>

                {/* Color Secundario */}
                <div>
                  <ColorPickerField
                    label="Color Secundario"
                    value={editFormData.color_secundario || '#1a73e8'}
                    defaultColor="#1a73e8"
                    onChange={(v) => handleEditInputChange('color_secundario', v)}
                    helpText="Acentos, badges de 'Matriz' y elementos de apoyo"
                  />
                  {editErrors.color_secundario && <p className="text-red-500 text-xs mt-1">{editErrors.color_secundario}</p>}
                </div>

                {/* Vista previa colores */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Vista Previa de Colores</label>
                  <div className="border-2 border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="px-4 py-3 text-white text-sm font-semibold flex items-center justify-between"
                      style={{ backgroundColor: editFormData.color_primario || '#3cbf9a' }}>
                      <span>Cabecera</span>
                      <div className="flex gap-1">
                        {[1,2,3].map(i => <div key={i} className="w-2 h-2 rounded-full bg-white opacity-60" />)}
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 space-y-2">
                      <button type="button" className="w-full text-white text-xs font-medium py-1.5 rounded-lg"
                        style={{ backgroundColor: editFormData.color_primario || '#3cbf9a' }}>
                        Botón primario
                      </button>
                      <button type="button" className="w-full text-white text-xs font-medium py-1.5 rounded-lg"
                        style={{ backgroundColor: editFormData.color_secundario || '#1a73e8' }}>
                        Botón secundario
                      </button>
                      <div className="flex gap-2 flex-wrap">
                        <span className="text-xs px-2 py-1 rounded-full font-medium"
                          style={{ backgroundColor: `${editFormData.color_primario || '#3cbf9a'}20`, color: editFormData.color_primario || '#3cbf9a' }}>
                          Activo
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full font-medium"
                          style={{ backgroundColor: `${editFormData.color_secundario || '#1a73e8'}20`, color: editFormData.color_secundario || '#1a73e8' }}>
                          Matriz
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Logo ────────────────────────────────────────────────── */}
                <div className="md:col-span-3 mt-2">
                  <h3 className="text-base font-bold text-gray-800 mb-1 pb-2 border-b-2 flex items-center" style={borderPrimary}>
                    <ImageIcon className="w-5 h-5 mr-2" style={textPrimary} />
                    Logo del Cliente
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    El logo se mostrará en el header de la aplicación cuando este cliente inicie sesión.
                    Se aplicará también a todos sus centros asociados.
                  </p>
                </div>

                <div className="md:col-span-2">
                  <LogoUploadField
                    clientId={editingClient.id}
                    logoArchivoId={editFormData.logo_archivo_id}
                    logoFilename={editFormData.logo_filename}
                    onUploaded={(id) => handleEditInputChange('logo_archivo_id', id)}
                  />
                </div>

                {/* Preview del header con logo */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Vista Previa del Header</label>
                  <div className="border-2 border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    <div
                      className="px-4 py-3 flex items-center justify-between"
                      style={{ backgroundColor: editFormData.color_primario || '#3cbf9a' }}
                    >
                      {/* Logo en miniatura */}
                      <div className="flex items-center">
                        {(() => {
                          const previewLogoSrc = editFormData.logo_archivo_id
                            ? `${API_URL}/api/archivos/${editFormData.logo_archivo_id}`
                            : editFormData.logo_filename
                              ? `${API_URL}/logos/${editFormData.logo_filename}`
                              : null;
                          return (
                            <div className="h-8 w-20 bg-white bg-opacity-20 rounded flex items-center justify-center overflow-hidden">
                              {previewLogoSrc ? (
                                <img
                                  src={previewLogoSrc}
                                  alt="Logo preview"
                                  className="max-h-full max-w-full object-contain p-1"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <span
                                className="text-white text-xs"
                                style={{ display: previewLogoSrc ? 'none' : 'flex' }}
                              >
                                logo.png
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-white bg-opacity-30" />
                        <div className="w-4 h-4 bg-white bg-opacity-20 rounded" />
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3 flex-shrink-0 rounded-b-2xl">
              <button onClick={() => setShowEditClientModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-sm">
                Cancelar
              </button>
              <button
                onClick={handleSaveClient}
                disabled={savingClient}
                className="px-6 py-2 text-white rounded-lg transition-all flex items-center text-sm"
                style={{ backgroundColor: savingClient ? '#9ca3af' : theme.primary }}
                onMouseEnter={(e) => !savingClient && (e.currentTarget.style.backgroundColor = theme.primaryDark)}
                onMouseLeave={(e) => !savingClient && (e.currentTarget.style.backgroundColor = theme.primary)}
              >
                {savingClient ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>Guardando...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />Guardar Cambios</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}